import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, IconButton, Menu, MenuItem, Skeleton, Typography } from "@mui/material";

import { ArrowDownIcon, ChatIcon, GlobeIcon, ProfileIcon } from "../../../../assets/icons/MUI/LayoutIcons";

import { logout } from "../../../../firebase/firebase";
import APP_ROUTES from "../../../../routers/routeConstants";
import LogoIcon from "../../assets/images/goalngo-nav-logo.png";
import useLanguageToggle from "../../../../locale/useChangeLang";
import { useProfileData } from "../../../../hooks/useProfileData";
import { deletePushNotifications } from "../../../../api/notifications";
import { useNotificationsHandler } from "../../hooks/useNotificationsHandler";

import SearchBar from "./SearchBar";
import MobileNavBar from "./MobileNavBar";
import Notifications from "../Notifications";
import { ArabicLogo, EnglishLogo } from "../../assets/icons/layout-icons";

type NavBarProps = {
  onOpenMobileNav?: () => void;
};

const NavBar = ({ onOpenMobileNav }: NavBarProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const navigate = useNavigate();
  const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null);
  const { toggleLanguage } = useLanguageToggle();

  const { data: userData, isLoading } = useProfileData();
  const { fullName } = userData;

  const myQueryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      setProfileAnchorEl(null);
      localStorage.removeItem("entityId");
      deletePushNotifications().catch(console.warn);
      await logout();
      myQueryClient.clear();
      navigate(APP_ROUTES.HOME);
      window.location.reload();
    } catch (error) {
      console.error("Error during logout:", error);
      localStorage.clear();
      window.location.href = APP_ROUTES.HOME;
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const isMenuOpen = Boolean(profileAnchorEl);

  const {
    notificationsOpen,
    anchorEl: notificationsAnchorEl,
    activeTab,
    lastSeen,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setActiveTab,
    handleOpenNotifications,
    handleCloseNotifications,
    hasNewNotifications,
  } = useNotificationsHandler();
  return (
    <Box component="nav" sx={styles.contentWrapper}>
      <Box sx={styles.innerContainer}>
        <Box sx={styles.innerContainerLeft}>
          <Link to={APP_ROUTES.DASHBOARD} style={styles.logoLink}>
            {currentLanguage === "ar" ? <ArabicLogo /> : <EnglishLogo />}
          </Link>
          <SearchBar />
        </Box>
        <Box sx={styles.innerContainerRight}>
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "9px", alignItems: "center" }}>
            <IconButton onClick={toggleLanguage} sx={styles.languageButton}>
              <Typography variant="body10Bold" sx={styles.languageButtonText}>
                {currentLanguage === "ar" ? "EN" : "AR"}
              </Typography>
              <GlobeIcon />
            </IconButton>
            <Box component={Link} to={APP_ROUTES.CHAT}>
              <ChatIcon />
            </Box>
            <Notifications
              handleOpenNotifications={handleOpenNotifications}
              notificationsOpen={notificationsOpen}
              anchorEl={notificationsAnchorEl}
              handleCloseNotifications={handleCloseNotifications}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              data={data}
              lastSeen={lastSeen}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              hasNewNotifications={hasNewNotifications}
            />
            <Button sx={styles.profileButton} onClick={handleProfileMenuOpen}>
              <ProfileIcon />
              {isLoading ? <Skeleton variant="text" width="40px" animation="wave" /> : fullName}
              <ArrowDownIcon />
            </Button>
            <Menu
              anchorEl={profileAnchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={isMenuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem sx={styles.profileButtonText} onClick={handleLogout}>
                {t("layout.nav.logout")}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>
      <MobileNavBar onOpenMobileNav={onOpenMobileNav} />
    </Box>
  );
};

export default NavBar;

const styles = {
  contentWrapper: {
    width: "100%",
    background: "#FFF",
    padding: "11px 0",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  innerContainer: {
    display: { xs: "none", lg: "flex" },
    justifyContent: "space-between",
    width: "100%",
    maxWidth: "1140px",
    margin: "0 auto",
    padding: { xs: "0 16px", lg: "0" },
  },
  innerContainerLeft: { display: "flex", alignItems: "center", gap: { xs: "16px", lg: "100px" } },
  showSideNavButton: { display: { xs: "inline-flex", lg: "none" } },
  logoLink: { height: "34px" },
  logo: {
    height: "34px",
    aspectRatio: "2.8",
    objectFit: "contain",
  },
  innerContainerRight: { display: "flex", gap: "9px", alignItems: "center" },
  languageButton: { position: "relative" },
  languageButtonText: {
    position: "absolute",
    zIndex: 1000,
    top: "2px",
    right: "0",
    backgroundColor: "var(--Green---100, #008B07)",
    padding: "2px",
    borderRadius: "50%",
    color: "#FFF",
  },
  profileButton: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
    color: "var(--Dark---100, #1A1A1A)",
    display: "flex",
    gap: "6px",
    borderRadius: "5px",
  },
  profileButtonText: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
    color: "var(--Dark---100, #1A1A1A)",
  },
};
